/**
 * Consent-gated analytics bootstrap (PostHog).
 *
 * PostHog is started imperatively — deliberately NOT via a React provider.
 * Wrapping the app in a provider keyed on consent remounted the whole tree
 * whenever the user accepted/changed cookie settings, wiping in-progress
 * screen state (e.g. a half-built course in the creation wizard — bug found
 * in manual testing, 2026-07-17). Consent changes must never touch the
 * component tree.
 *
 * The library is loaded via dynamic import only after the user accepts
 * analytics cookies, so no analytics code executes — and no `ph_*` cookies
 * are set — before consent. Revoking consent opts out and resets; cookie
 * cleanup is handled by `cookieConsent.ts`.
 */
import { getCookieConsent, subscribeCookieConsent } from './cookieConsent';

let started = false;

/**
 * Pages that never reach analytics. The unsubscribe link carries a signed token
 * in its URL — a credential that also encodes the person's address — so a
 * pageview there would ship both to PostHog. Landing on one skips starting
 * PostHog for that page load; if it is already running, its events are dropped.
 */
const UNTRACKED_PATHS = ['/email/unsubscribe'];

function isUntrackedPath(pathname: string): boolean {
  return UNTRACKED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

async function start(): Promise<void> {
  const token = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
  if (started || !token || isUntrackedPath(window.location.pathname)) return;
  started = true;
  const { default: posthog } = await import('posthog-js');
  posthog.init(token, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: '2026-01-30',
    before_send: (event) => {
      const url = event?.properties?.$current_url;
      if (
        typeof url === 'string' &&
        isUntrackedPath(new URL(url, window.location.origin).pathname)
      ) {
        return null;
      }
      return event;
    },
  });
}

async function stop(): Promise<void> {
  if (!started) return;
  started = false;
  const { default: posthog } = await import('posthog-js');
  posthog.opt_out_capturing();
  posthog.reset();
}

/** Call once at boot: starts analytics if already consented and follows
 *  future consent changes. Never triggers a React re-render. */
export function initAnalyticsConsentGate(): void {
  if (getCookieConsent()?.analytics) void start();
  subscribeCookieConsent((consent) => {
    if (consent?.analytics) void start();
    else void stop();
  });
}
